import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { 
  Settings,
  Key,
  Users,
  Shield,
  HardDrive,
  Clock,
  Calendar,
  Folder,
  RefreshCw,
  Lock,
  Hash,
  AlertTriangle,
  List,
  Loader2,
  Mail,
  User,
  UserPlus,
  KeySquare
} from 'lucide-react';
import { NomenclatorPage } from './NomenclatorPage';
import { TwoFactorAuthPage } from './TwoFactorAuthPage';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatDate } from '../../utils/format';
import { fetchWithAuth } from '../../hooks/useAuth';

interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

interface SecuritySettings {
  passwordMinLength: number;
  passwordRequireNumbers: boolean;
  passwordRequireSymbols: boolean;
  sessionTimeout: number;
  maxLoginAttempts: number;
  ipWhitelist: string[];
}

interface BackupSettings {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  retention: number;
  lastBackup: string | null;
  nextBackup: string | null;
  backupLocation: string;
}

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = React.useState<
    'users' | 'security' | 'backup' | 'nomenclator' | '2fa'
  >('users');

  // Fetch admin users
  const { data: adminUsers } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const response = await fetchWithAuth('/api/admin/settings/users');
      if (!response.ok) throw new Error('Eroare la încărcarea utilizatorilor');
      return response.json() as Promise<AdminUser[]>;
    },
    enabled: selectedTab === 'users',
  });

  // Fetch security settings
  const { data: securitySettings } = useQuery({
    queryKey: ['security-settings'],
    queryFn: async () => {
      const response = await fetchWithAuth('/api/admin/settings/security');
      if (!response.ok) throw new Error('Eroare la încărcarea setărilor de securitate');
      return response.json() as Promise<SecuritySettings>;
    },
    enabled: selectedTab === 'security',
  });

  // Fetch backup settings
  const { data: backupSettings } = useQuery({
    queryKey: ['backup-settings'],
    queryFn: async () => {
      const response = await fetchWithAuth('/api/admin/settings/backup');
      if (!response.ok) throw new Error('Eroare la încărcarea setărilor de backup');
      return response.json() as Promise<BackupSettings>;
    },
    enabled: selectedTab === 'backup',
  });

  // Create admin user mutation
  const createAdminUser = useMutation({
    mutationFn: async (data: { email: string; name?: string }) => {
      const response = await fetchWithAuth('/api/admin/settings/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Eroare la crearea utilizatorului');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Utilizator creat cu succes');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Update security settings mutation
  const updateSecuritySettings = useMutation({
    mutationFn: async (data: Partial<SecuritySettings>) => {
      const response = await fetchWithAuth('/api/admin/settings/security', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Eroare la actualizarea setărilor de securitate');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security-settings'] });
      toast.success('Setări de securitate actualizate cu succes');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Update backup settings mutation
  const updateBackupSettings = useMutation({
    mutationFn: async (data: Partial<BackupSettings>) => {
      const response = await fetchWithAuth('/api/admin/settings/backup', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Eroare la actualizarea setărilor de backup');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-settings'] });
      toast.success('Setări de backup actualizate cu succes');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Trigger manual backup mutation
  const triggerBackup = useMutation({
    mutationFn: async () => {
      const response = await fetchWithAuth('/api/admin/settings/backup/trigger', {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Eroare la inițierea backup-ului');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-settings'] });
      toast.success('Backup inițiat cu succes');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <Settings className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Setări Sistem</h1>
            <p className="text-sm text-gray-500">
              Configurare și administrare sistem
            </p>
          </div>
        </div>
      </div>

      {/* Settings Navigation */}
      <Card className="bg-white shadow-sm">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Button
              variant={selectedTab === 'users' ? 'default' : 'outline'}
              onClick={() => setSelectedTab('users')}
              className="flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              Utilizatori
            </Button>
            <Button
              variant={selectedTab === 'security' ? 'default' : 'outline'}
              onClick={() => setSelectedTab('security')}
              className="flex items-center gap-2"
            >
              <Shield className="w-4 h-4" />
              Securitate
            </Button>
            <Button
              variant={selectedTab === 'backup' ? 'default' : 'outline'}
              onClick={() => setSelectedTab('backup')}
              className="flex items-center gap-2"
            >
              <HardDrive className="w-4 h-4" />
              Backup
            </Button>
            <Button
              variant={selectedTab === 'nomenclator' ? 'default' : 'outline'}
              onClick={() => setSelectedTab('nomenclator')}
              className="flex items-center gap-2"
            >
              <List className="w-4 h-4" />
              Nomenclator
            </Button>
            <Button
              variant={selectedTab === '2fa' ? 'default' : 'outline'}
              onClick={() => setSelectedTab('2fa')}
              className="flex items-center gap-2"
            >
              <KeySquare className="w-4 h-4" />
              Autentificare 2FA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User Management */}
      {selectedTab === 'users' && adminUsers && (
        <>
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Utilizatori Admin
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Nume</TableHead>
                    <TableHead>Ultima Autentificare</TableHead>
                    <TableHead>Data Creării</TableHead>
                    <TableHead>Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adminUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.name || 'N/A'}</TableCell>
                      <TableCell>
                        {user.lastLoginAt
                          ? formatDate(user.lastLoginAt)
                          : 'Niciodată'}
                      </TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell>
                        <Button variant="link">Resetare Parolă</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Adaugă Utilizator Admin
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  createAdminUser.mutate({
                    email: formData.get('email') as string,
                    name: formData.get('name') as string,
                  });
                }}
              >
                <div>
                  <label htmlFor="userEmail" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </label>
                  <Input 
                    id="userEmail"
                    name="email" 
                    type="email" 
                    required 
                    className="mt-1"
                    placeholder="Introduceți adresa de email"
                  />
                </div>
                <div>
                  <label htmlFor="userName" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Nume
                  </label>
                  <Input 
                    id="userName"
                    name="name"
                    className="mt-1"
                    placeholder="Introduceți numele utilizatorului"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={createAdminUser.isPending}
                  className="flex items-center gap-2"
                >
                  {createAdminUser.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Se adaugă...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      Adaugă Utilizator
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </>
      )}

      {/* Security Settings */}
      {selectedTab === 'security' && securitySettings && (
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Setări Securitate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4">
              <div>
                <label htmlFor="passwordMinLength" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Lungime Minimă Parolă
                </label>
                <Input
                  id="passwordMinLength"
                  type="number"
                  value={securitySettings.passwordMinLength}
                  onChange={(e) =>
                    updateSecuritySettings.mutate({
                      passwordMinLength: Number(e.target.value),
                    })
                  }
                  className="mt-1"
                  placeholder="Introduceți lungimea minimă a parolei"
                />
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50">
                <input
                  type="checkbox"
                  id="requireNumbers"
                  checked={securitySettings.passwordRequireNumbers}
                  onChange={(e) =>
                    updateSecuritySettings.mutate({
                      passwordRequireNumbers: e.target.checked,
                    })
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="requireNumbers" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  Necesită Numere în Parolă
                </label>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50">
                <input
                  type="checkbox"
                  id="requireSymbols"
                  checked={securitySettings.passwordRequireSymbols}
                  onChange={(e) =>
                    updateSecuritySettings.mutate({
                      passwordRequireSymbols: e.target.checked,
                    })
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="requireSymbols" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Necesită Simboluri în Parolă
                </label>
              </div>
              <div>
                <label htmlFor="sessionTimeout" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Timeout Sesiune (minute)
                </label>
                <Input
                  id="sessionTimeout"
                  type="number"
                  value={securitySettings.sessionTimeout}
                  onChange={(e) =>
                    updateSecuritySettings.mutate({
                      sessionTimeout: Number(e.target.value),
                    })
                  }
                  className="mt-1"
                  placeholder="Introduceți timeout-ul sesiunii"
                />
              </div>
              <div>
                <label htmlFor="maxLoginAttempts" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Încercări Maxime de Autentificare
                </label>
                <Input
                  id="maxLoginAttempts"
                  type="number"
                  value={securitySettings.maxLoginAttempts}
                  onChange={(e) =>
                    updateSecuritySettings.mutate({
                      maxLoginAttempts: Number(e.target.value),
                    })
                  }
                  className="mt-1"
                  placeholder="Introduceți numărul maxim de încercări"
                />
              </div>
              <div>
                <label htmlFor="ipWhitelist" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  IP Whitelist
                </label>
                <Input
                  id="ipWhitelist"
                  value={securitySettings.ipWhitelist.join(', ')}
                  onChange={(e) =>
                    updateSecuritySettings.mutate({
                      ipWhitelist: e.target.value.split(',').map((ip) => ip.trim()),
                    })
                  }
                  className="mt-1"
                  placeholder="IP-uri separate prin virgulă"
                />
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Backup Settings */}
      {selectedTab === 'backup' && backupSettings && (
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardDrive className="w-5 h-5" />
              Setări Backup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4">
                <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50">
                  <input
                    type="checkbox"
                    id="backupEnabled"
                    checked={backupSettings.enabled}
                    onChange={(e) =>
                      updateBackupSettings.mutate({
                        enabled: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="backupEnabled" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Backup Automat
                  </label>
                </div>
                <div>
                  <label htmlFor="backupFrequency" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Frecvență
                  </label>
                  <select
                    id="backupFrequency"
                    value={backupSettings.frequency}
                    onChange={(e) =>
                      updateBackupSettings.mutate({
                        frequency: e.target.value as 'daily' | 'weekly' | 'monthly',
                      })
                    }
                    className="w-full rounded-md border p-2 mt-1"
                  >
                    <option value="daily">Zilnic</option>
                    <option value="weekly">Săptămânal</option>
                    <option value="monthly">Lunar</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="backupRetention" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Retenție (număr de backup-uri)
                  </label>
                  <Input
                    id="backupRetention"
                    type="number"
                    value={backupSettings.retention}
                    onChange={(e) =>
                      updateBackupSettings.mutate({
                        retention: Number(e.target.value),
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="backupLocation" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Folder className="w-4 h-4" />
                    Locație Backup
                  </label>
                  <Input
                    id="backupLocation"
                    value={backupSettings.backupLocation}
                    onChange={(e) =>
                      updateBackupSettings.mutate({
                        backupLocation: e.target.value,
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <div className="text-sm font-medium">Ultimul Backup</div>
                  <div>
                    {backupSettings.lastBackup
                      ? formatDate(backupSettings.lastBackup)
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium">Următorul Backup</div>
                  <div>
                    {backupSettings.nextBackup
                      ? formatDate(backupSettings.nextBackup)
                      : 'N/A'}
                  </div>
                </div>
                <Button
                  onClick={() => triggerBackup.mutate()}
                  disabled={triggerBackup.isPending}
                  className="flex items-center gap-2"
                >
                  {triggerBackup.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Se execută...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Backup Manual
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
      )}

      {/* Nomenclator Settings */}
      {selectedTab === 'nomenclator' && <NomenclatorPage />}
      {selectedTab === '2fa' && <TwoFactorAuthPage />}
    </div>
  );
}
